import batchService from '@/api/batch'
import sampleService from '@/api/sample'
import webSocketPrintService from '@/api/webSocketPrint'
import PrintWebSocketService from '@/services/PrintWebSocketService'

import {
  BATCH_SET,
  BATCH_SET_LOADING_STATUS,
  BATCH_SAMPLES_SET,
  BATCH_CLEAR_ALL,
  BATCH_CLEAR_OFFSET,
  BATCH_INCREASE_OFFSET,
  BATCH_SET_PRINTING_STATUS,
  BATCH_SET_LIST,
  BATCH_FILTER_SHOW_OFF,
  BATCH_SET_UNPRINTED,
  BATCH_SET_PRINTED,
  BATCH_SET_SAMPLES,
  BATCH_SAMPLE_SHOW_DETAILS,
  BATCH_SAMPLE_SORT_LIST,
  BATCH_SET_SORT_PRINT_SHOW_OFF,
  BATCH_SET_SORT_PRINT_SELECT,
  BATCH_CLOSE_ALL_SORT_PRINT_SHOW_OFF,
  BATCH_SET_SORT_TO_SAMPLES,
  BATCH_SET_CARD_STATUS,
  BATCH_UPDATE_DETAILS_OPENED,
  BATCH_APPEND_DETAILS_SAMPLES,
  BATCH_SET_CURRENT_TAB,
  BATCH_SET_ALL_BATCHES,
  BATCH_SAMPLES_SET_PRINTING_STATUS,
  BATCH_SET_SELECTED_SAMPLES,
  SET_SELECTED_SAMPLES,
  SAMPLES_SET_LOADING_STATUS,
} from '../types'
import { getCookie } from '../../utils/auth-utils'
import notify from '../../api/notification'
import analytics from '../../api/analytics'
import strings from '@/strings'
import { sortColumn } from '../../utils/sort-columns'
import { deepCloneObj } from '../../utils/object-utils'
import { toggleDefaultData } from '../../api/toggle'

const upcomingBatchMapper = item => {
  item.expanded = false
  item.loading = false
  item.samples = []
  item.printSortShowOff = true
  item.printSortSelected = {
    value: 'os_lis',
    label: 'OS Lis',
  }

  return item
}

const getSampleSortColumn = value => (value === 'inclusion_order' ? null : value || null)

const batchNaturalOrderOptions = {
  numeric: true,
  sensitivity: 'base'
}

const getBatchMnemonic = batch => (batch?.mnemonic || batch?.label || '').toString()

const getBatchSequence = batch => (batch?.sequence || '').toString()

const compareBatchesByMnemonicAndSequence = (batchA, batchB) => {
  const mnemonicComparison = getBatchMnemonic(batchA).localeCompare(getBatchMnemonic(batchB), undefined, batchNaturalOrderOptions)

  if (mnemonicComparison !== 0) {
    return mnemonicComparison
  }

  return getBatchSequence(batchA).localeCompare(getBatchSequence(batchB), undefined, batchNaturalOrderOptions)
}

const sortBatchesByMnemonicAndSequence = batches => (batches || []).slice().sort(compareBatchesByMnemonicAndSequence)

export const normalizeAolwsBatchSample = item => {
  item.bar_code = item.bar_code || item.barcode
  item.patient_name = item.patient_name || item.order?.patient?.name
  item.number_alvaro_order = item.number_alvaro_order || item.order?.alvaroId
  item.os_lis = item.os_lis || item.order?.lisId
  item.id_material = item.id_material || item.material?.id || item.materialId
  item.material_description =
    item.material_description || item.material?.description || item.mnemonic
  item.printed_status = true

  return item
}

const getSampleKey = sample => sample?.bar_code || sample?.barcode || sample?.sample_id

const state = {
  printedBatches: [],
  unprintedBatches: [],
  bathSelectedSamples: [],
  selectedSamples: [],
  batchesLoadingStatus: 'STATUS_INITIAL',
  batchesPrintingStatus: 'STATUS_INITIAL',
  batchesSamplesPrintingStatus: 'STATUS_INITIAL',
  samplesLoadingStatus: 'STATUS_INITIAL',
  batches: {
    aol20: {
      printed: [],
      unprinted: [],
    },
    aolWs: {
      printed: [],
      unprinted: [],
    },
  },
  currentTab: 'aol20',
  offset: 0,
  batchSamples: [],
  profile: getCookie('profile') || null,
  filterBy: null,
  startDate: null,
  endDate: null,
  q: '',
  lastReordenationBatch: {
    sort: null,
    column: null,
  },
  patientName: '',
  packagingType: null,
  batchLabel: '',
  codeOriginLis: '',
  codeOriginAlvaro: '',
  totalBatchesCount: 0,
  filterShowOff: false,
  isBatchDetailsVisible: false,
  batchOpenIndex: 0,
  isBatchOpenCardUnprinted: true,
  batchDetailsOpened: {},
  collectionPointName: '',
  source: '',
  webSocketStatus: {
    connectionState: 'disconnected',
    queueStatus: null,
    printers: [],
    defaultPrinter: null,
    isConfigured: false,
    lastError: null,
    reconnectAttempts: 0,
    hasNewVersion: false,
    availablePorts: [8181, 5050, 49200, 5000, 3000],
    currentPortIndex: 0,
    currentPort: 8181,
  },
}
const mutations = {
  [BATCH_SET_SELECTED_SAMPLES](state, samples) {
    state.bathSelectedSamples = samples
  },
  [SET_SELECTED_SAMPLES](state, samples) {
    state.selectedSamples = samples
  },
  [BATCH_SET_LOADING_STATUS](state, batchesLoadingStatus) {
    state.batchesLoadingStatus = batchesLoadingStatus
  },
  [BATCH_SAMPLES_SET_PRINTING_STATUS](state, status) {
    state.batchesSamplesPrintingStatus = status
  },
  [BATCH_SET](state, batches) {
    state.batches = state.batches.concat(batches)
  },
  [BATCH_SAMPLES_SET](state, object) {
    state.batches[object.index].samples = object.samples
    state.batches[object.index].loading = false
  },
  [BATCH_CLEAR_ALL](state) {
    state.batches = {}
    state.isBatchDetailsVisible = false
  },
  [BATCH_CLEAR_OFFSET](state) {
    state.offset = 0
  },
  [BATCH_INCREASE_OFFSET](state) {
    state.offset++
  },
  [BATCH_SET_PRINTING_STATUS](state, batchesPrintingStatus) {
    state.batchesPrintingStatus = batchesPrintingStatus
  },
  [BATCH_UPDATE_DETAILS_OPENED](state, batch) {
    state.batchDetailsOpened = batch
  },
  [BATCH_SAMPLE_SORT_LIST](state, param) {
    state.lastReordenationBatch = param
    if (state.isBatchOpenCardUnprinted) {
      let temporaryArray = state.batchesUnprinted[state.batchOpenIndex].samples
      state.batches[state.currentTab].unprinted[state.batchOpenIndex].samples = []

      if (param.sort === 'asc') {
        state.batches[state.currentTab].unprinted[state.batchOpenIndex].samples =
          temporaryArray.sort(
            sortColumn(param.column, param.columnChild, 'one-field-level')
          )
      } else {
        state.batches[state.currentTab].unprinted[state.batchOpenIndex].samples =
          temporaryArray.reverse(
            sortColumn(param.column, param.columnChild, 'one-field-level')
          )
      }
    } else {
      let temporaryArray =
        state.batches[state.currentTab].printed[state.batchOpenIndex].samples
      state.batches[state.currentTab].printed[state.batchOpenIndex].samples = []

      if (param.sort === 'asc') {
        state.batches[state.currentTab].printed[state.batchOpenIndex].samples =
          temporaryArray.sort(
            sortColumn(param.column, param.columnChild, 'one-field-level')
          )
      } else {
        state.batches[state.currentTab].printed[state.batchOpenIndex].samples =
          temporaryArray.reverse(
            sortColumn(param.column, param.columnChild, 'one-field-level')
          )
      }
    }
  },
  [BATCH_FILTER_SHOW_OFF](state, value) {
    state.filterShowOff = value
  },
  [BATCH_SET_LIST](state, query) {
    state.offset = 0
    state.startDate = query.startDate
    state.endDate = query.endDate
    state.patientName = query.patientName
    state.packagingType = query.packagingType
    state.batchLabel = query.batchLabel
    state.codeOriginLis = query.codeOriginLis
    state.codeOriginAlvaro = query.codeOriginAlvaro
    state.collectionPointName = query.collectionPointName
    state.source = query.source
  },
  [BATCH_SET_UNPRINTED](state, batchesUnprinted) {
    state.isBatchDetailsVisible = false
    state.batches[state.currentTab].unprinted =
      state.batches[state.currentTab].unprinted.concat(batchesUnprinted)
  },
  [BATCH_SET_PRINTED](state, batchesPrinted) {
    state.isBatchDetailsVisible = false
    state.batches[state.currentTab].printed =
      state.batches[state.currentTab].printed.concat(batchesPrinted)
  },
  [BATCH_SET_SAMPLES](state, object) {
    if (state.isBatchOpenCardUnprinted) {
      state.batches[state.currentTab].unprinted[object.index].samples = object.samples
      state.batches[state.currentTab].unprinted[object.index].loading = false
    } else {
      state.batches[state.currentTab].printed[object.index].samples = object.samples
      state.batches[state.currentTab].printed[object.index].loading = false
    }
  },
  [BATCH_SAMPLE_SHOW_DETAILS](state, object) {
    if (state.isBatchOpenCardUnprinted) {
      state.batches[state.currentTab].unprinted[object.printTagIndex].samples[
        object.sampleIndex
      ].expanded =
        !state.batches[state.currentTab].unprinted[object.printTagIndex].samples[
          object.sampleIndex
        ].expanded
    } else {
      state.batches[state.currentTab].printed[object.printTagIndex].samples[
        object.sampleIndex
      ].expanded =
        !state.batches[state.currentTab].printed[object.printTagIndex].samples[
          object.sampleIndex
        ].expanded
    }
  },
  [BATCH_SET_SORT_PRINT_SHOW_OFF](state, index) {
    state.batches[state.currentTab].unprinted[index].printSortShowOff =
      !state.batches[state.currentTab].unprinted[index].printSortShowOff
  },
  [BATCH_SET_SORT_PRINT_SELECT](state, object) {
    const toggleData = toggleDefaultData()
    if (toggleData?.feature.nonSegregatedBatchList) {
      if (object.Unprinted) {
        state.unprintedBatches[object.index].printSortSelected = object.column
      } else {
        state.printedBatches[object.index].printSortSelected = object.column
      }
    } else if (state.isBatchOpenCardUnprinted) {
      state.batches[state.currentTab].unprinted[object.index].printSortSelected =
        object.column
    } else {
      state.batches[state.currentTab].printed[object.index].printSortSelected =
        object.column
    }
  },
  [BATCH_CLOSE_ALL_SORT_PRINT_SHOW_OFF](state) {
    if (state.isBatchOpenCardUnprinted) {
      state.batches[state.currentTab].unprinted = state.batches[
        state.currentTab
      ].unprinted.map(item => {
        item.printSortShowOff = true
        return item
      })
    } else {
      state.batches[state.currentTab].printed = state.batches[
        state.currentTab
      ].printed.map(item => {
        item.printSortShowOff = true
        return item
      })
    }
  },
  [BATCH_SET_SORT_TO_SAMPLES](state, param) {
    state.lastReordenationBatch = param
    if (param.sort === 'asc') {
      state.batchDetailsOpened.samples = state.batchDetailsOpened.samples.sort(
        sortColumn(param.column, param.columnChild, 'one-field-level')
      )
    } else {
      state.batchDetailsOpened.samples = state.batchDetailsOpened.samples.reverse(
        sortColumn(param.column, param.columnChild, 'one-field-level')
      )
    }
  },
  [BATCH_APPEND_DETAILS_SAMPLES](state, samples) {
    const currentSamples = state.batchDetailsOpened.samples || []
    const existingSamples = new Set(currentSamples.map(getSampleKey).filter(Boolean))
    const newSamples = (samples || []).filter(sample => {
      const key = getSampleKey(sample)
      return key && !existingSamples.has(key)
    })

    state.batchDetailsOpened = {
      ...state.batchDetailsOpened,
      samples: [...currentSamples, ...newSamples],
    }
  },
  [BATCH_SET_ALL_BATCHES](state, batches) {
    const toggleData = toggleDefaultData()

    if (toggleData?.feature.nonSegregatedBatchList) {
      state.printedBatches = sortBatchesByMnemonicAndSequence(batches.printed).map(item => {
        item.isPrinted = true
        return upcomingBatchMapper(item)
      })
      state.unprintedBatches = sortBatchesByMnemonicAndSequence(batches.unprinted).map(item => {
        item.isPrinted = false
        return upcomingBatchMapper(item)
      })
    } else {
      if (batches.aol20.unprinted) {
        batches.aol20.unprinted = sortBatchesByMnemonicAndSequence(batches.aol20.unprinted).map(upcomingBatchMapper)
      }

      if (batches.aol20.printed) {
        batches.aol20.printed = sortBatchesByMnemonicAndSequence(batches.aol20.printed).map(upcomingBatchMapper)
      }

      if (batches.aolWs.unprinted) {
        batches.aolWs.unprinted = sortBatchesByMnemonicAndSequence(batches.aolWs.unprinted).map(upcomingBatchMapper)
      }

      if (batches.aolWs.printed) {
        batches.aolWs.printed = sortBatchesByMnemonicAndSequence(batches.aolWs.printed).map(upcomingBatchMapper)
      }

      state.batches = batches
    }
  },
  [BATCH_SET_CARD_STATUS](state, value) {
    state.isBatchOpenCardUnprinted = value
  },
  [BATCH_SET_CURRENT_TAB](state, value) {
    state.currentTab = value
  },
  [SAMPLES_SET_LOADING_STATUS](state, status) {
    state.samplesLoadingStatus = status
  },
  WEBSOCKET_SET_STATUS(state, status) {
    state.webSocketStatus.connectionState = status
  },
  WEBSOCKET_SET_QUEUE_STATUS(state, status) {
    state.webSocketStatus.queueStatus = status
  },
  WEBSOCKET_SET_PRINTERS(state, printers) {
    state.webSocketStatus.printers = printers
  },
  WEBSOCKET_SET_DEFAULT_PRINTER(state, printer) {
    state.webSocketStatus.defaultPrinter = printer
    state.webSocketStatus.isConfigured = !!printer
  },
  WEBSOCKET_SET_LAST_ERROR(state, error) {
    state.webSocketStatus.lastError = error
  },
  WEBSOCKET_UPDATE_PRINTERS(state, printers) {
    state.webSocketStatus.printers = printers
  },
  WEBSOCKET_INCREMENT_RECONNECT(state) {
    state.webSocketStatus.reconnectAttempts++
  },
  WEBSOCKET_SET_IF_HAS_NEW_VERSION(state, version) {
    state.webSocketStatus.hasNewVersion = version.hasNewVersion || false
    state.webSocketStatus.actualVersion = version.version
  },
  WEBSOCKET_SET_CURRENT_PORT(state, port) {
    state.webSocketStatus.currentPort = port
  },
  WEBSOCKET_SET_CURRENT_PORT_INDEX(state, index) {
    state.webSocketStatus.currentPortIndex = index
  },
  WEBSOCKET_RESET_PORT_INDEX(state) {
    state.webSocketStatus.currentPortIndex = 0
    state.webSocketStatus.currentPort = state.webSocketStatus.availablePorts[0]
  },
}

const getters = {
  printedBatches: state => state.printedBatches,
  unprintedBatches: state => state.unprintedBatches,
  batchesAmount: state => state.printedBatches.length + state.unprintedBatches.length,
  bathSelectedSamples: state => state.bathSelectedSamples,
  selectedSamples: state => state.selectedSamples,

  batchesShowGrid: state => {
    if (state.batches[state.currentTab]) {
      if (state.batches[state.currentTab].printed.length > 0) return true
      if (state.batches[state.currentTab].unprinted.length > 0) return true
    }
    return false
  },
  batchesCurrentTab: state => {
    return state.currentTab
  },
  batchesCounters: state => {
    const batches = state.batches
    let aol20 = batches.aol20
    let aolWs = batches.aolWs
    if (aol20) aol20 = aol20.printed.length + aol20.unprinted.length
    if (aolWs) aolWs = aolWs.printed.length + aolWs.unprinted.length
    return { aol20, aolWs }
  },
  batchesLoadingStatus: state => {
    return state.batchesLoadingStatus
  },
  isLoadingBatchSamples: state => state.samplesLoadingStatus === 'STATUS_LOADING',
  batches: state => {
    return state.batches
  },
  hasBatches: state => {
    return state.batches && state.batches.length > 0
  },
  batchesLoading: state => {
    return state.batchesLoadingStatus === 'STATUS_INITIAL'
  },
  batchSamples: state => {
    return state.batchSamples
  },
  hasBatchSamples: state => {
    return state.batchSamples && state.batchSamples.length > 0
  },
  batchesPrintingStatus: state => {
    return state.batchesPrintingStatus
  },
  isBatchFilterShowing: state => {
    return state.filterShowOff
  },
  batchesUnprinted: state => {
    if (state.batches[state.currentTab]) return state.batches[state.currentTab].unprinted
  },
  batchesPrinted: state => {
    if (state.batches[state.currentTab]) return state.batches[state.currentTab].printed
  },
  hasBatchesUnprinted: state => {
    return (
      state.batches[state.currentTab].unprinted &&
      state.batches[state.currentTab].unprinted > 0
    )
  },
  hasBatchesPrinted: state => {
    return (
      state.batches[state.currentTab].printed &&
      state.batches[state.currentTab].printed > 0
    )
  },
  isBatchDetailsShowing: state => {
    return state.isBatchDetailsVisible
  },
  currentBatchOpenIndex: state => {
    return state.batchOpenIndex
  },
  isBatchCardOpenUnprinted: state => {
    return state.isBatchOpenCardUnprinted
  },
  batchSamplesSorted: state => {
    return state.batchDetailsOpened
  },
  batchesSamplesPrintingStatus: state => {
    return state.batchesSamplesPrintingStatus
  },
  webSocketStatus: state => state.webSocketStatus,
  isPrinterConfigured: state => state.webSocketStatus.isConfigured,
  availablePrinters: state => state.webSocketStatus.printers,
  defaultPrinter: state => state.webSocketStatus.defaultPrinter,
}

const actions = {
  batcheSetTab({ commit }, tab) {
    commit(BATCH_SET_CURRENT_TAB, tab)
  },
  batchGetBatches({ commit, state }) {
    commit(BATCH_SET_LOADING_STATUS, 'STATUS_INITIAL')

    const toggleData = toggleDefaultData()

    if (toggleData?.feature.nonSegregatedBatchList) {
      return batchService
        .getBatches(
          state.offset,
          strings.listsLimits.batches,
          state.startDate,
          state.endDate,
          state.patientName,
          state.packagingType,
          state.batchLabel,
          state.codeOriginLis,
          state.codeOriginAlvaro,
          state.collectionPointName,
          state.source
        )
        .then(response => {
          commit(BATCH_SET_ALL_BATCHES, response.data)
          commit(BATCH_SET_LOADING_STATUS, 'STATUS_END')
        })
        .catch(() => {
          commit(BATCH_SET_LOADING_STATUS, 'STATUS_FAILED')
        })
    }

    return batchService
      .getBatchesGrouped(
        strings.listsLimits.batches,
        state.offset,
        state.startDate,
        state.endDate,
        state.patientName,
        state.packagingType,
        state.batchLabel,
        state.codeOriginLis,
        state.codeOriginAlvaro,
        state.collectionPointName
      )
      .then(response => {
        commit(BATCH_SET_ALL_BATCHES, response.data)
        commit(BATCH_SET_LOADING_STATUS, 'STATUS_END')
      })
      .catch(() => {
        commit(BATCH_SET_LOADING_STATUS, 'STATUS_FAILED')
      })
  },
  setBatchSelectedSamples({ commit }, samples) {
    commit(BATCH_SET_SELECTED_SAMPLES, samples)
  },
  setSelectedSamples({ commit }, samples) {
    commit(SET_SELECTED_SAMPLES, samples)
  },
  clearSelectedSamples({ commit }) {
    commit(SET_SELECTED_SAMPLES, [])
  },
  // remove this method on new tracking for aolws integration
  getBatchesCards({ commit, state, getters }) {
    commit(BATCH_SET_LOADING_STATUS, 'STATUS_INITIAL')
    return batchService
      .getBatches(
        state.offset,
        strings.listsLimits.batches,
        state.startDate,
        state.endDate,
        state.patientName,
        state.packagingType,
        state.batchLabel,
        state.codeOriginLis,
        state.codeOriginAlvaro,
        state.collectionPointName
      )
      .then(response => {
        commit(BATCH_SET_ALL_BATCHES, response.data)
        const counter = getters.batchesCounters
        if (counter.aol20 > 0 || counter.aolws > 0) {
          if (
            state.startDate ||
            state.endDate ||
            state.patientName ||
            state.packagingType ||
            state.batchLabel ||
            state.codeOriginLis ||
            state.codeOriginAlvaro
          ) {
            let query = {
              startDate: state.startDate,
              endDate: state.endDate,
              patientName: state.patientName,
              packagingType: state.packagingType,
              batchLabel: state.batchLabel,
              codeOriginLis: state.codeOriginLis,
              codeOriginAlvaro: state.codeOriginAlvaro,
            }
            analyticsOnFetchingFromSearchBatches(query)
          }
          if (counter.aol20 + counter.aolws === strings.listsLimits.batches) {
            commit(BATCH_SET_LOADING_STATUS, 'STATUS_SUCCESS')
          } else {
            commit(BATCH_SET_LOADING_STATUS, 'STATUS_END')
          }
          commit(BATCH_INCREASE_OFFSET)
        } else {
          commit(BATCH_SET_LOADING_STATUS, 'STATUS_END')
        }
      })
      .catch(() => {
        commit(BATCH_SET_LOADING_STATUS, 'STATUS_FAILED')
      })
  },
  updateBatchDetailsOpened({ commit }, batch) {
    commit(BATCH_UPDATE_DETAILS_OPENED, batch)
  },
  setWaitingBatchesLoadingStatus({ commit }) {
    commit(BATCH_SET_LOADING_STATUS, 'STATUS_WAITING')
  },
  clearAllBatchesData({ commit }) {
    commit(BATCH_SET_LOADING_STATUS, 'STATUS_INITIAL')
    commit(BATCH_CLEAR_ALL)
    commit(BATCH_CLEAR_OFFSET)
  },
  printBatchSamples({ commit }, batch) {
    commit(BATCH_SAMPLES_SET_PRINTING_STATUS, 'STATUS_LOADING')
    return batchService
      .printBatchSamples(batch)
      .then(response => {
        commit(BATCH_SAMPLES_SET_PRINTING_STATUS, 'STATUS_INITIAL')
        return response.status
      })
      .catch(error => {
        if (error.response.status === 428) {
          commit(BATCH_SAMPLES_SET_PRINTING_STATUS, 'STATUS_MISSING_CLIENT')
          notify({
            title: 'AVISO:',
            message:
              'Erro ao conectar à impressora. Por favor, verifique se o cliente de impressão está instalado ou faça login novamente.',
          })
          return error.response.status
        } else {
          commit(BATCH_SAMPLES_SET_PRINTING_STATUS, 'STATUS_FAILED')
          notify({
            title: 'AVISO:',
            message: 'Erro ao imprimir etiquetas. Por favor, tente novamente.',
          })
          return error.response.status
        }
      })
  },
  webSocketPrintBatchSamples: async function ({ commit, state }, batch) {
    commit(BATCH_SAMPLES_SET_PRINTING_STATUS, 'STATUS_LOADING')

    let barcodeResponse

    if (batch.source === 'AOLWS') {
      barcodeResponse = await webSocketPrintService.getAOLWSBarcode({
        barcodeList: batch.samples.map(sample => sample.bar_code),
        batch: [batch.barcode],
        filterBy: 'BATCH',
        printBatch: false,
        isPrinted: batch.onlySamplesPrinted,
      })
    } else {
      barcodeResponse = await webSocketPrintService.getBarcode({
        barcodeList: batch.samples.map(sample => sample.bar_code),
        batch: [batch.barcode],
        filterBy: 'BATCH',
        printBatch: false,
        isPrinted: batch.onlySamplesPrinted,
        sampleSortColumn: getSampleSortColumn(batch?.printSortSelected?.value),
      })
    }

    batch['labelCode'] = barcodeResponse.data

    try {
      // Verifica se tem impressora configurada
      if (!state.webSocketStatus.isConfigured) {
        notify({
          type: 'warning',
          title: 'Atenção',
          message: 'Selecione uma impressora para continuar',
        })

        throw new Error('printer_config_required')
      }

      // Tenta imprimir via WebSocket
      const response = await PrintWebSocketService.printBatchSamples({
        ...batch,
        printer: state.webSocketStatus.defaultPrinter,
      })

      if (response.status === 'SUCCESS') {
        commit(BATCH_SAMPLES_SET_PRINTING_STATUS, 'STATUS_INITIAL')
        return response.status
      }
    } catch (error) {
      console.error('Erro na impressão:', error)

      if (error.message === 'printer_config_required') {
        commit(BATCH_SAMPLES_SET_PRINTING_STATUS, 'STATUS_INITIAL')
        return
      }

      if (error.message.includes('impressora')) {
        commit(BATCH_SAMPLES_SET_PRINTING_STATUS, 'STATUS_MISSING_CLIENT')
        notify({
          title: 'AVISO:',
          message: 'Configure uma impressora antes de imprimir',
        })
      } else {
        commit(BATCH_SAMPLES_SET_PRINTING_STATUS, 'STATUS_FAILED')
        notify({
          title: 'AVISO:',
          message: 'Erro ao imprimir etiquetas. Por favor, tente novamente.',
        })
      }
      throw error
    }
  },
  setFilterShowOff({ commit }, value) {
    commit(BATCH_FILTER_SHOW_OFF, value)
  },
  selectBatchList({ commit }, batchList) {
    commit(BATCH_SET_LOADING_STATUS, 'STATUS_INITIAL')
    commit(BATCH_SET_LIST, batchList)
  },
  showSampleRow({ commit }, object) {
    commit(BATCH_SAMPLE_SHOW_DETAILS, object)
  },
  sortBatchSamplesList({ commit }, param) {
    commit(BATCH_SAMPLE_SORT_LIST, param)
  },
  printReceiptBatchSamples({ state }, { samples, batch }) {
    return new Promise(resolve => {
      let ids
      let codeOriginAlvaroList
      if (samples.length > 0) {
        ids = samples.map(function (elem) {
          return elem.os_order_id
        })
        codeOriginAlvaroList = samples.map(function (elem) {
          return elem.number_alvaro_order.toString()
        })
      }
      sampleService
        .printReceipt({
          orderIds: ids,
          codeOriginAlvaroList: codeOriginAlvaroList,
          batchId: batch.id || batch.batchIdentifier,
        })
        .then(response => {
          let blob = new Blob([response.data], {
            type: 'application/pdf',
          })
          let date = new Date()
          let filename = date.toISOString() + '.pdf'
          // Check if is IE or Edge
          if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, filename)
          } else {
            let link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = filename
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            document.body.appendChild(link)
          }
          resolve()
        })
        .catch(() => {
          notify({
            title: 'AVISO: ',
            message: 'Não foi possível gerar o recibo. Por favor, tente novamente.',
          })
          resolve()
        })
    })
  },
  printAndTransmit({ commit }, batch) {
    commit(BATCH_SET_PRINTING_STATUS, 'STATUS_PRINTING_BATCH')
    return batchService
      .transmitAndPrint(batch)
      .then(response => {
        commit(BATCH_SET_PRINTING_STATUS, 'STATUS_INITIAL')
        return response.status
      })
      .catch(error => {
        if (error.response.status === 428) {
          commit(BATCH_SET_PRINTING_STATUS, 'STATUS_MISSING_CLIENT')
          notify({
            title: 'AVISO:',
            message:
              'Erro ao conectar à impressora. Por favor, verifique se o cliente de impressão está instalado ou faça login novamente.',
          })
          return error.response.status
        } else {
          commit(BATCH_SET_PRINTING_STATUS, 'STATUS_INITIAL')
          notify({
            title: 'AVISO:',
            message: 'Erro ao imprimir etiquetas. Por favor, tente novamente.',
          })
          return error.response.status
        }
      })
  },
  webSocketPrintAndTransmit: async function ({ commit, state }, batch) {
    commit(BATCH_SET_PRINTING_STATUS, 'STATUS_LOADING')

    let barcodeResponse

    if (batch.source === 'AOLWS') {
      barcodeResponse = await webSocketPrintService.getAOLWSBarcode({
        barcodeList: batch.samples.map(sample => sample.bar_code),
        batch: [batch.barcode],
        filterBy: 'BATCH',
        printBatch: true,
        isPrinted: batch.onlySamplesPrinted,
        printOnlyBatchTag: batch.printOnlyBatchTag,
      })
    } else {
      barcodeResponse = await webSocketPrintService.getBarcode({
        barcodeList: batch.samples.map(sample => sample.bar_code),
        batch: [batch.barcode],
        filterBy: 'BATCH',
        printBatch: true,
        isPrinted: batch.onlySamplesPrinted,
        printOnlyBatchTag: batch.printOnlyBatchTag,
        sampleSortColumn: getSampleSortColumn(batch?.printSortSelected?.value),
      })
    }

    batch['labelCode'] = barcodeResponse.data

    try {
      // Verifica se tem impressora configurada
      if (!state.webSocketStatus.isConfigured) {
        notify({
          type: 'warning',
          title: 'Atenção',
          message: 'Selecione uma impressora para continuar',
        })

        throw new Error('printer_config_required')
      }

      // Tenta imprimir via WebSocket
      const response = await PrintWebSocketService.printBatchSamples({
        ...batch,
        printer: state.webSocketStatus.defaultPrinter,
      })

      if (response.status === 'SUCCESS') {
        commit(BATCH_SET_PRINTING_STATUS, 'STATUS_INITIAL')
        return response.status
      }
    } catch (error) {
      console.error('Erro na impressão:', error)

      if (error.message === 'printer_config_required') {
        commit(BATCH_SET_PRINTING_STATUS, 'STATUS_INITIAL')
        return
      }

      if (error.message.includes('impressora')) {
        commit(BATCH_SET_PRINTING_STATUS, 'STATUS_MISSING_CLIENT')
        notify({
          title: 'AVISO:',
          message: 'Configure uma impressora antes de imprimir',
        })
      } else {
        commit(BATCH_SET_PRINTING_STATUS, 'STATUS_FAILED')
        notify({
          title: 'AVISO:',
          message: 'Erro ao imprimir etiquetas. Por favor, tente novamente.',
        })
      }
      throw error
    }
  },
  sortPrintBatchShowOff({ commit }, index) {
    commit(BATCH_SET_SORT_PRINT_SHOW_OFF, index)
  },
  sortPrintBatchSetColumn({ commit }, object) {
    commit(BATCH_SET_CARD_STATUS, object.Unprinted)
    commit(BATCH_SET_SORT_PRINT_SELECT, object)
  },
  closeAllSortPrintShowOff({ commit }) {
    commit(BATCH_CLOSE_ALL_SORT_PRINT_SHOW_OFF)
  },
  batchSetSortToSamples({ commit }, param) {
    commit(BATCH_SET_SORT_TO_SAMPLES, param)
  },
  batchSetCardStatus({ commit }, value) {
    commit(BATCH_SET_CARD_STATUS, value)
  },
  mockFakeBatchesForTutorial({ commit, state }) {
    let fakeBatch = [
      {
        placeExecutionId: 35,
        quantitySamples: null,
        id: 15,
        label: 'ALP.A',
        barcode: 'LT0A00000260',
        place_execution: null,
        packaging: null,
        packagingId: 27,
        created_at: '2020-04-20T15:34:53.506Z',
        total_samples: 1,
        state: 'CLOSED',
        code: null,
        transmitted_at: null,
        color: null,
        packagingAcronym: null,
        destinationAcronym: null,
        sequence: '1',
        doc_delivered: true,
        docRequired: false,
        isPrinted: false,
        source: 'AOL2',
        capability: '5',
        expanded: false,
        loading: false,
        samples: [],
        printSortShowOff: true,
        printSortSelected: {
          value: 'os_lis',
          label: 'OS Lis',
        },
      },
    ]
    const tab = state.currentTab
    if (
      state.batches[tab] &&
      state.batches[tab].unprinted.length === 0 &&
      state.batchesLoadingStatus !== 'STATUS_INITIAL'
    ) {
      let mock = deepCloneObj(fakeBatch[0])
      commit(BATCH_SET_UNPRINTED, mock)
    }
    if (
      state.batches[tab] &&
      state.batches[tab].printed.length === 0 &&
      state.batchesLoadingStatus !== 'STATUS_INITIAL'
    ) {
      let mock = deepCloneObj(fakeBatch[0])
      mock.isPrinted = true
      commit(BATCH_SET_PRINTED, mock)
    }
  },
  async fetchBatchSamples({ commit, state }, { batchId, filters }) {
    commit('SAMPLES_SET_LOADING_STATUS', 'STATUS_LOADING')

    try {
      if (batchId?.startsWith('LT')) {
        const response = await sampleService.aolwsSampleByBatch(batchId, filters)

        commit('BATCH_UPDATE_DETAILS_OPENED', {
          ...state.batchDetailsOpened,
          samples: response.data.map(normalizeAolwsBatchSample),
        })
      } else {
        const response = await batchService.getBatchSamples({ batchId, filters })

        commit('BATCH_UPDATE_DETAILS_OPENED', {
          ...state.batchDetailsOpened,
          samples: response.data,
        })
      }

      commit('SAMPLES_SET_LOADING_STATUS', 'STATUS_INITIAL')
    } catch (error) {
      console.error('Error fetching batch samples.', error)

      commit('SAMPLES_SET_LOADING_STATUS', 'STATUS_FAILED')
    }
  },
  appendBatchDetailsSamples({ commit }, { samples, isWs }) {
    const normalizedSamples = isWs
      ? (samples || []).map(normalizeAolwsBatchSample)
      : samples

    commit(BATCH_APPEND_DETAILS_SAMPLES, normalizedSamples)
  },
  clearBatchDetailsSamples({ commit }) {
    commit('BATCH_UPDATE_DETAILS_OPENED', {
      ...state.batchDetailsOpened,
      samples: [],
    })
  },
  async initializeWebSocket({ commit, state, dispatch }) {
    try {
      commit('WEBSOCKET_SET_STATUS', 'connecting')
      commit('WEBSOCKET_RESET_PORT_INDEX')

      // Passa referência do dispatch para o serviço
      PrintWebSocketService.setStoreDispatch(dispatch)

      const ws = await PrintWebSocketService.connectWithPortFallback(
        state.webSocketStatus.availablePorts
      )

      ws.onmessage = event => {
        dispatch('handleWebSocketMessage', event)
      }

      ws.onclose = () => {
        commit('WEBSOCKET_SET_STATUS', 'disconnected')
        dispatch('handleWebSocketReconnect')
      }

      // Não muda para connected aqui - será mudado na validação do plugin

      // Carrega as impressoras e verifica a padrão salva
      const availablePrinters = await PrintWebSocketService.getPrinters()
      commit('WEBSOCKET_SET_PRINTERS', availablePrinters)

      const savedPrinter = localStorage.getItem('defaultPrinter')
      if (savedPrinter && availablePrinters.includes(savedPrinter)) {
        commit('WEBSOCKET_SET_DEFAULT_PRINTER', savedPrinter)
      }

      const hasNewVersion = await PrintWebSocketService.checkForNewVersion()
      if (hasNewVersion) {
        commit('WEBSOCKET_SET_IF_HAS_NEW_VERSION', hasNewVersion)
      }
    } catch (error) {
      commit('WEBSOCKET_SET_STATUS', 'disconnected')
      commit('WEBSOCKET_SET_LAST_ERROR', error.message)

      setTimeout(() => {
        dispatch('initializeWebSocket')
      }, 10000)

      throw error
    }
  },
  handleWebSocketMessage({ dispatch }, event) {
    try {
      let data

      try {
        data = JSON.parse(event.data)
      } catch (parseError) {
        return
      }

      if (Array.isArray(data)) {
        dispatch('updatePrinters', data)
      } else if (data.type === 'PRINT_RESULT') {
        dispatch('handlePrintResult', data)
      }
    } catch (e) {
      console.error('Erro ao processar mensagem:', e)
    }
  },
  handleWebSocketReconnect({ state, dispatch, commit }) {
    if (state.webSocketStatus.reconnectAttempts < 5) {
      setTimeout(
        () => {
          dispatch('initializeWebSocket')
        },
        5000 * (state.webSocketStatus.reconnectAttempts + 1)
      )

      commit('WEBSOCKET_INCREMENT_RECONNECT')
    }
  },
  async loadPrinters({ commit }) {
    try {
      const availablePrinters = await PrintWebSocketService.getPrinters()
      commit('WEBSOCKET_SET_PRINTERS', availablePrinters)

      // Verifica impressora padrão salva
      const savedPrinter = localStorage.getItem('defaultPrinter')
      if (savedPrinter && availablePrinters.includes(savedPrinter)) {
        commit('WEBSOCKET_SET_DEFAULT_PRINTER', savedPrinter)
      }
    } catch (error) {
      notify({
        type: 'error',
        title: 'Erro',
        message: 'Falha ao carregar impressoras: ' + error.message,
      })
      throw error
    }
  },
  setDefaultPrinter({ commit }, printer) {
    localStorage.setItem('defaultPrinter', printer)
    commit('WEBSOCKET_SET_DEFAULT_PRINTER', printer)
  },
  syncPrinterFromStorage({ commit, state }) {
    if (!state.webSocketStatus.defaultPrinter) {
      const savedPrinter = localStorage.getItem('defaultPrinter')
      if (savedPrinter) {
        commit('WEBSOCKET_SET_DEFAULT_PRINTER', savedPrinter)
        return savedPrinter
      }
    }
    return state.webSocketStatus.defaultPrinter
  },
  updateWebSocketStatus({ commit }, status) {
    commit('WEBSOCKET_SET_STATUS', status)
  },
  updateQueueStatus({ commit }, status) {
    commit('WEBSOCKET_SET_QUEUE_STATUS', status)
  },
  async validatePrintingSystem({ state }, printJob) {
    if (state.webSocketStatus.connectionState !== 'connected') {
      throw new Error('Serviço de impressão não está conectado')
    }

    if (!state.webSocketStatus.isConfigured) {
      throw new Error('Impressora não configurada')
    }

    return await webSocketPrintService.addToQueue({
      ...printJob,
      printer: state.webSocketStatus.defaultPrinter,
    })
  },
  setLastError({ commit }, error) {
    commit('WEBSOCKET_SET_LAST_ERROR', error)
  },
  updatePrinters({ commit }, printers) {
    commit('WEBSOCKET_UPDATE_PRINTERS', printers)
  },
  handlePrintResult({ commit }, result) {
    if (result.success) {
      commit(BATCH_SET_PRINTING_STATUS, 'STATUS_INITIAL')
    } else {
      commit(BATCH_SET_PRINTING_STATUS, 'STATUS_ERROR')
      notify({
        type: 'error',
        title: 'Erro',
        message: 'Falha na impressão',
      })
    }
  },
  setCurrentPort({ commit }, port) {
    commit('WEBSOCKET_SET_CURRENT_PORT', port)
  },
  setCurrentPortIndex({ commit }, index) {
    commit('WEBSOCKET_SET_CURRENT_PORT_INDEX', index)
  },
  resetPortIndex({ commit }) {
    commit('WEBSOCKET_RESET_PORT_INDEX')
  },
}

let analyticsOnFetchingFromSearchBatches = query => {
  analytics.pushAnalyticsEvent('ga-search', {
    startDate: state.startDate,
    endDate: state.endDate,
    patientName: state.patientName,
    packagingType: state.packagingType,
    batchLabel: state.batchLabel,
    codeOriginLis: state.codeOriginLis,
    codeOriginAlvaro: state.codeOriginAlvaro,
  })
}

export default {
  state,
  mutations,
  actions,
  getters,
}
export { mutations, actions, getters }
